import React, { useEffect, useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDashboardStore } from '@store/useDashboardStore'
import { formatDate } from '@utils/formatters'
import RiskSummaryCards from './RiskSummaryCards'
import RiskInsights from './RiskInsights'
import ProjectRiskTable from './ProjectRiskTable'
import RiskCharts from './RiskCharts'
import ReportHistory from './ReportHistory'
import './RiskExposureReport.scss'

/**
 * RiskExposureReport - AI-powered risk exposure analysis dashboard widget
 *
 * Displays comprehensive risk analysis across all active projects.
 * Features background report generation with callback pattern.
 *
 * @param {string} className - Additional CSS class
 */
const RiskExposureReport = ({ className = '' }) => {
	const [pollingInterval, setPollingInterval] = useState(null)
	const [selectedHistoryId, setSelectedHistoryId] = useState(null)

	const {
		riskExposureReport,
		riskExposureStatus,
		riskExposureHistory,
		riskExposureLoading,
		riskExposureError,
		fetchRiskExposureReport,
		fetchRiskExposureStatus,
		fetchRiskExposureHistory,
		generateRiskExposureReport,
		loadRiskExposureReportById,
		clearRiskExposureError
	} = useDashboardStore()

	// Initial load
	useEffect(() => {
		fetchRiskExposureReport()
		fetchRiskExposureStatus()
		fetchRiskExposureHistory()
	}, [fetchRiskExposureReport, fetchRiskExposureStatus, fetchRiskExposureHistory])

	// Poll for status when generation is in progress
	useEffect(() => {
		if (riskExposureStatus?.is_generating && !pollingInterval) {
			const interval = setInterval(async () => {
				const status = await fetchRiskExposureStatus()
				if (!status?.is_generating) {
					// Generation completed - fetch the new report
					await fetchRiskExposureReport()
					await fetchRiskExposureHistory()
					clearInterval(interval)
					setPollingInterval(null)
				}
			}, 5000) // Poll every 5 seconds

			setPollingInterval(interval)
		}

		return () => {
			if (pollingInterval) {
				clearInterval(pollingInterval)
			}
		}
	}, [riskExposureStatus?.is_generating, pollingInterval, fetchRiskExposureStatus, fetchRiskExposureReport, fetchRiskExposureHistory])

	// Handle generate button click
	const handleGenerate = useCallback(async () => {
		await generateRiskExposureReport()
		// Start polling
		const status = await fetchRiskExposureStatus()
		if (status?.is_generating) {
			// Polling will be handled by the useEffect above
		}
	}, [generateRiskExposureReport, fetchRiskExposureStatus])

	// Handle history selection
	const handleHistorySelect = useCallback(async (reportId) => {
		if (reportId === 'latest') {
			setSelectedHistoryId(null)
			await fetchRiskExposureReport()
		} else {
			setSelectedHistoryId(reportId)
			await loadRiskExposureReportById(reportId)
		}
	}, [fetchRiskExposureReport, loadRiskExposureReportById])

	// Memoize report content
	const reportContent = useMemo(() => {
		if (!riskExposureReport?.report_content) return null
		return typeof riskExposureReport.report_content === 'string'
			? JSON.parse(riskExposureReport.report_content)
			: riskExposureReport.report_content
	}, [riskExposureReport])

	// Determine component state
	const isGenerating = riskExposureStatus?.is_generating
	const hasReport = !!riskExposureReport && reportContent
	const hasFailed = riskExposureStatus?.status === 'failed'

	// Render empty state
	if (!hasReport && !isGenerating && !riskExposureLoading && !hasFailed) {
		return (
			<div className={`risk-exposure-report risk-exposure-report--empty ${className}`}>
				<div className="risk-exposure-report__header">
					<h2 className="risk-exposure-report__title">Risk Exposure Report</h2>
				</div>
				<div className="risk-exposure-report__empty-state">
					<div className="risk-exposure-report__empty-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
						</svg>
					</div>
					<h3>No Risk Exposure Report Generated</h3>
					<p>
						Generate a comprehensive AI-powered risk analysis across all your active projects.
					</p>
					<button
						className="risk-exposure-report__generate-btn"
						onClick={handleGenerate}
						disabled={riskExposureLoading}
					>
						Generate Report
					</button>
				</div>
			</div>
		)
	}

	// Render generating state
	if (isGenerating) {
		return (
			<div className={`risk-exposure-report risk-exposure-report--generating ${className}`}>
				<div className="risk-exposure-report__header">
					<h2 className="risk-exposure-report__title">Risk Exposure Report</h2>
					<span className="risk-exposure-report__status-badge risk-exposure-report__status-badge--generating">
						Generating
					</span>
				</div>
				<div className="risk-exposure-report__generating-state">
					<div className="risk-exposure-report__spinner">
						<div className="spinner"></div>
					</div>
					<h3>Report Generation In Progress</h3>
					<p>
						Your risk exposure report is being generated. This may take a few minutes.
						You can navigate away and check back later.
					</p>
					{riskExposureStatus?.started_at && (
						<p className="risk-exposure-report__started-at">
							Started: {formatDate(riskExposureStatus.started_at, 'PPpp')}
						</p>
					)}
				</div>
			</div>
		)
	}

	// Render failed state
	if (hasFailed || riskExposureError) {
		return (
			<div className={`risk-exposure-report risk-exposure-report--failed ${className}`}>
				<div className="risk-exposure-report__header">
					<h2 className="risk-exposure-report__title">Risk Exposure Report</h2>
				</div>
				<div className="risk-exposure-report__failed-state">
					<div className="risk-exposure-report__error-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<circle cx="12" cy="12" r="10" />
							<path d="M15 9l-6 6M9 9l6 6" />
						</svg>
					</div>
					<h3>Report Generation Failed</h3>
					<p>
						{riskExposureError || 'Unable to generate report. Please try again after some time.'}
					</p>
					<button
						className="risk-exposure-report__retry-btn"
						onClick={handleGenerate}
						disabled={riskExposureLoading}
					>
						Retry
					</button>
				</div>
			</div>
		)
	}

	// Render report
	return (
		<div className={`risk-exposure-report ${className}`}>
			<div className="risk-exposure-report__header">
				<div className="risk-exposure-report__header-left">
					<h2 className="risk-exposure-report__title">Risk Exposure Report</h2>
					{riskExposureReport?.generation_completed_at && (
						<span className="risk-exposure-report__timestamp">
							Last Generated: {formatDate(riskExposureReport.generation_completed_at, 'PPpp')}
						</span>
					)}
				</div>
				<div className="risk-exposure-report__header-actions">
					<ReportHistory
						history={riskExposureHistory}
						selectedId={selectedHistoryId}
						onSelect={handleHistorySelect}
					/>
					<button
						className="risk-exposure-report__regenerate-btn"
						onClick={handleGenerate}
						disabled={riskExposureLoading || isGenerating}
					>
						{riskExposureLoading ? 'Generating...' : 'Regenerate'}
					</button>
				</div>
			</div>

			{riskExposureLoading ? (
				<div className="risk-exposure-report__loading">
					<div className="spinner"></div>
					<p>Loading report...</p>
				</div>
			) : (
				<div className="risk-exposure-report__content">
					{/* Summary Cards */}
					<RiskSummaryCards summary={reportContent?.summary} />

					{/* AI Insights */}
					<RiskInsights
						insights={reportContent?.insights}
						projectObservations={reportContent?.project_observations}
					/>

					{/* Charts Section */}
					<RiskCharts visualizations={reportContent?.visualizations} />

					{/* Project Risk Table */}
					<ProjectRiskTable observations={reportContent?.project_observations} />
				</div>
			)}
		</div>
	)
}

RiskExposureReport.propTypes = {
	className: PropTypes.string
}

export default RiskExposureReport
